import { CheckCircleOutlined } from '@ant-design/icons';
import commaNumber from 'comma-number';
import styled from 'styled-components';

import {
  CenterFlex,
  Flex,
  HorizontalScrollView,
  Space,
} from '@/components/base';
import Button from '@/components/Button/Button';
import { ProgressIndicator } from '@/components/Chart/ProgressBar/ProgressBar';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';

export type SimpleCol = {
  columnKey: string;
  colWidth: number;
  render?: (value: string, row: SimpleRow) => string;
};

export type SimpleRowData = {
  columnKey: string;
  rawData?: string;
};

export type SimpleRow = {
  rowKey: number;
  rowData: SimpleRowData[];
};

export enum UPLOAD_STATE {
  READY,
  UPLOADING,
  COMPLETE,
}

interface Props {
  columns: SimpleCol[];
  total: number;
  datas: SimpleRow[];
  uploading: UPLOAD_STATE;
  progress: {
    current: number;
    total: number;
  };
  confirmUpload: () => void;
  closeUploadModal: () => void;
}

const UploadPreview = (props: Props) => {
  const color = useColor();

  return (
    <Wrapper>
      {props.uploading === UPLOAD_STATE.UPLOADING && (
        <ProgressIndicator {...props.progress} />
      )}
      {props.uploading === UPLOAD_STATE.READY && (
        <>
          <Flex j="space-between" a="center">
            <Typo type="body2" weight="sb" color={color.gray700}></Typo>
            <Typo type="body2" weight="r" color={color.gray700}>
              Total{' '}
              <span className="feedback-count">{commaNumber(props.total)}</span>{' '}
              feedback found (Up to 100 preview data is displayed)
            </Typo>
          </Flex>
          <Space h="16px" />
          <HorizontalScrollView hideScrollbar style={{ maxHeight: '100%' }}>
            <div>
              <DataField g={8}>
                {props.columns.map((col) => (
                  <div key={col.columnKey} style={{ width: col.colWidth }}>
                    {col.columnKey}
                  </div>
                ))}
              </DataField>
              <div className="data-rows-wrapper">
                {props.datas.map((row) => {
                  return (
                    <DataRow
                      key={row.rowKey}
                      row={row}
                      columns={props.columns}
                    />
                  );
                })}
              </div>
            </div>
          </HorizontalScrollView>
          <Button
            sizeType="m"
            onClick={props.confirmUpload}
            style={{
              width: '100px',
              height: '36px',
              position: 'absolute',
              right: '32px',
              bottom: '32px',
            }}
          >
            Confirm
          </Button>
        </>
      )}
      {props.uploading === UPLOAD_STATE.COMPLETE && (
        <CenterFlex style={{ height: 600 }} d="column">
          <CheckCircleOutlined
            style={{ fontSize: 128, color: color.primary100 }}
          />
          <Space h={48} />
          <Heading type="h2">Upload Complete</Heading>
          <Space h={24} />
          <Button sizeType="l" onClick={props.closeUploadModal}>
            Confirm
          </Button>
        </CenterFlex>
      )}
    </Wrapper>
  );
};

export default UploadPreview;

const DataRow = (props: { row: SimpleRow; columns: SimpleCol[] }) => {
  return (
    <DataRowWrapper a="center" g={8}>
      {props.columns.map((col) => {
        const cell = props.row.rowData.find(
          (c) => c.columnKey === col.columnKey,
        ) || {
          columnKey: col.columnKey,
          rawData: '',
        };
        const displayString = col.render
          ? col.render?.(cell.rawData || '', props.row)
          : cell.rawData;
        return (
          <CellStyle
            key={col.columnKey}
            style={{
              width: col.colWidth,
            }}
          >
            <Typo ellipsis>{displayString}</Typo>
          </CellStyle>
        );
      })}
    </DataRowWrapper>
  );
};

const CellStyle = styled(Flex)`
  padding: 2px 0px;
`;

const Wrapper = styled.div`
  padding: 40px 32px;
  height: calc(100% - 88px);

  .data-rows-wrapper {
    padding-bottom: 50px;
    height: calc(100% - 44px);
  }
`;

const DataField = styled(Flex)`
  align-items: center;
  height: 44px;
  background: ${(props) => props.theme.colors.gray200};
  border: 1px solid ${(props) => props.theme.colors.gray400};
  border-width: 1px 0 1px 0;
  padding: 0 16px;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  color: ${(props) => props.theme.colors.gray800};
`;

const DataRowWrapper = styled(Flex)`
  width: 100%;
  height: 24px;
  border-bottom: 1px solid ${(props) => props.theme.colors.gray400};
  padding-left: 16px;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: ${(props) => props.theme.colors.gray800};

  & > div {
    padding-right: 10px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    word-break: break-all;
  }
`;
